import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import DateTimePicker from '../../../components/DateTimePicker';
import UnitProgramDropdown from '../../../components/dispatch/UnitProgramDropdown';
import ActingBodiesCommandDropdown from '../../../components/Dropdowns/ActingBodiesCommandDropdown';
import FormInput from '../../../components/FormInput';
import EquipmentsDropdown from './EquipmentDropDown';
import { useAuth } from '../../../context/Auth';
import { translate } from '../../../services/translate';
import { WindowInformation } from '../../../services/window-information';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import CustomButton from '../../../components/CustomButton';
import MyAppText from '../../../components/MyAppText';
import AddEquipmentByCategoryModal from './AddEquipmentByCategoryModal';
import { ScrollView } from 'react-native-web-hover';
import Badge from '../../../components/Badge';
import { dispatchService, SimplifiedEquipments } from '../../../services/central-api/dispatch';
import { UseFormReturn, useWatch } from 'react-hook-form';
import ActingBodiesDropdown from '../../../components/Dropdowns/ActingBodiesDropdown';
import { UnitValuesForm } from './UnitForm';
import ActingBodiesUnitsDropdown from '../../../components/Dropdowns/ActingBodiesUnitsDropdown';
import UnitNameDropdown from '../../../components/dispatch/UnitNameDropdown';

interface UnitFormDetailsProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<UnitValuesForm, any, any>;
    unitId?: number;
}

export default function UnitFormDetails({
    form,
    unitId,
}: UnitFormDetailsProps) {
    const { user: authenticatedUser } = useAuth();
    const windowInfo = WindowInformation();
    const { styles, theme } = useStyles(stylesheet);

    const createdAt = form.watch('createdAt');
    const [estimatedStartZIndex, setEstimatedStartZIndex] = useState<number>(2);
    const [estimatedFinishZIndex, setEstimatedFinishZIndex] = useState<number>(1);

    const [addByCategoryModal, setAddByCategoryModal] = useState<boolean>(false);
    const [groupedEquipments, setGroupedEquipments] = useState<{ name: string; quantity: number; equipmentsIds: number[]; }[]>([]);

    const [selectableEquipments, setSelectableEquipments] = useState<SimplifiedEquipments[]>([]);
    const [hasEquipmentCategories, setHasEquipmentCategories] = useState<boolean>(false);

    const actingBodyId = useWatch({
        control: form.control,
        name: 'actingBodyId'
    });

    const equipments = useWatch({
        control: form.control,
        name: 'equipments'
    });

    async function getEquipments({ actingBodyId }: { actingBodyId: number; }) {
        try {
            const equipments = await dispatchService.getUnitsSimplifiedEquipments(actingBodyId, unitId);
            setSelectableEquipments(equipments);
        } catch (err) {
            console.error(err);
        }
    }

    useEffect(() => {
        if (actingBodyId && !form.watch('definitiveFinish')) {
            setHasEquipmentCategories(false);
            getEquipments({ actingBodyId: actingBodyId });
        }
    }, [actingBodyId, unitId]);

    useEffect(() => {
        setHasEquipmentCategories(selectableEquipments.filter(e => e.equipmentCategory).length > 0);

        const equipments = selectableEquipments.filter(e => form.watch('equipments').includes(e.id));
        const itensWithoutCategory = equipments.filter(e => !e.equipmentCategory).map(e => {
            return { name: e.name, quantity: 0, equipmentsIds: [e.id] };
        }) || [];
        const itensWithCategory = equipments.filter(e => e.equipmentCategory) || [];

        const counting = itensWithCategory.reduce((acc, el) => {
            if (!el.equipmentCategory || !el.id) {
                return acc;
            }

            const categoryName = el.equipmentCategory.name;
            if (!acc[categoryName]) {
                acc[categoryName] = { quantity: 0, equipmentsIds: [] };
            }

            acc[categoryName].quantity++;
            acc[categoryName].equipmentsIds.push(el.id);

            return acc;
        }, {} as Record<string, { quantity: number; equipmentsIds: number[]; }>);

        const itensForList = Object.entries(counting).map(([name, data]) => ({
            name,
            quantity: data.quantity,
            equipmentsIds: data.equipmentsIds
        }));

        setGroupedEquipments([...itensWithoutCategory, ...itensForList]);

    }, [equipments, selectableEquipments]);

    if (form.watch('definitiveFinish')) {
        return <View style={{ rowGap: 15, flex: 1 }}>
            {authenticatedUser?.isAdmin
                || authenticatedUser?.permissions.dispatch_manage_acting_body_units
                || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_units ?
                <View style={{
                    flexDirection: 'row',
                    gap: 16,
                    zIndex: 6,
                    flexWrap: 'wrap',
                }}>
                    <FormInput
                        label={translate('actingBody')}
                        form={form}
                        fieldName='actingBodyUnit.actingBody.name'
                        disabled={true}
                        labelStyle={styles.fieldsLabelStyle}
                        viewStyle={[windowInfo.isMobile ? { width: '100%' } : { flex: 1 }]}
                    />
                    <FormInput
                        label={translate('unit')}
                        form={form}
                        fieldName='actingBodyUnit.name'
                        disabled={true}
                        labelStyle={styles.fieldsLabelStyle}
                        viewStyle={[windowInfo.isMobile ? { width: '100%' } : { flex: 1 }]}
                    />
                </View>
                : null
            }
            <FormInput
                label={translate('command')}
                form={form}
                fieldName='actingBodyCommand.name'
                disabled={true}
                labelStyle={styles.fieldsLabelStyle}
            />
            <FormInput
                label={translate('name')}
                form={form}
                fieldName='unitName.name'
                disabled={true}
                labelStyle={styles.fieldsLabelStyle}
            />
            <FormInput
                label={translate('unitProgram')}
                form={form}
                fieldName='unitProgram.name'
                disabled={true}
                labelStyle={styles.fieldsLabelStyle}
            />
            <View style={styles.datePickerStyle(estimatedStartZIndex, windowInfo.isMobile)}>
                <DateTimePicker
                    date={form.watch('estimatedStart.date')}
                    onChangeDate={(value) => form.setValue('estimatedStart.date', value)}
                    time={form.watch('estimatedStart.hour')}
                    onChangeTime={(value) => form.setValue('estimatedStart.hour', value)}
                    label={translate('estimatedStart')}
                    disabled={true}
                    labelStyle={styles.fieldsLabelStyle}
                />
            </View>
            <View style={styles.datePickerStyle(estimatedFinishZIndex, windowInfo.isMobile)}>
                <DateTimePicker
                    date={form.watch('estimatedFinish.date')}
                    onChangeDate={(value) => form.setValue('estimatedFinish.date', value)}
                    time={form.watch('estimatedFinish.hour')}
                    onChangeTime={(value) => form.setValue('estimatedFinish.hour', value)}
                    label={translate('estimatedFinish')}
                    disabled={true}
                    labelStyle={styles.fieldsLabelStyle}
                />
            </View>
            <FormInput
                label={translate('description')}
                form={form}
                fieldName='description'
                multiline={true}
                numberOfLines={5}
                viewStyle={{ flex: 1 }}
                disabled={true}
                labelStyle={styles.fieldsLabelStyle}
            />
        </View>;
    }

    return <View style={{ rowGap: 10, flex: 1 }}>
        {authenticatedUser?.isAdmin
            || authenticatedUser?.permissions.dispatch_manage_acting_body_units
            || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_units ?
            <View style={{
                flexDirection: 'row',
                gap: 16,
                zIndex: 6,
                flexWrap: 'wrap',
            }}>
                {unitId ? <FormInput
                    label={translate('actingBody')}
                    form={form}
                    fieldName='actingBodyUnit.actingBody.name'
                    disabled={true}
                    labelStyle={styles.fieldsLabelStyle}
                    viewStyle={[windowInfo.isMobile ? { width: '100%' } : { flex: 1 }]}
                />
                    : <ActingBodiesDropdown
                        form={form}
                        fieldName='actingBodyId'
                        includeAllOption={false}
                        disabled={!!unitId || !(authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_units)}
                        zIndex={5}
                        required={true}
                        labelStyle={styles.fieldsLabelStyle}
                        viewStyle={[{ zIndex: 5, gap: 8 }, windowInfo.isMobile ? { width: '100%' } : { flex: 1 }]}
                    />
                }
                {unitId ? <FormInput
                    label={translate('unit')}
                    form={form}
                    fieldName='actingBodyUnit.name'
                    disabled={true}
                    labelStyle={styles.fieldsLabelStyle}
                    viewStyle={[windowInfo.isMobile ? { width: '100%' } : { flex: 1 }]}
                /> : <ActingBodiesUnitsDropdown
                    form={form}
                    fieldName='actingBodyUnitId'
                    actingBodyId={actingBodyId}
                    zIndex={4}
                    disabled={!!unitId}
                    required={true}
                    labelStyle={styles.fieldsLabelStyle}
                    viewStyle={[{ zIndex: 4, gap: 8 }, windowInfo.isMobile ? { width: '100%' } : { flex: 1 }]}
                />}
            </View>
            : null
        }
        <ActingBodiesCommandDropdown
            form={form}
            fieldName='actingBodyCommandId'
            actingBodyId={actingBodyId}
            disabled={false}
            zIndex={5}
            required={true}
            labelStyle={styles.fieldsLabelStyle}
            viewStyle={{ gap: 8 }}
        />
        <UnitNameDropdown
            form={form}
            fieldName='unitNameId'
            actingBodyId={actingBodyId}
            disabled={!!unitId}
            includeDeleted={!!unitId}
            zIndex={4}
            required={true}
            labelStyle={styles.fieldsLabelStyle}
            viewStyle={{ gap: 8 }}
        />
        <UnitProgramDropdown
            form={form}
            fieldName='unitProgramId'
            actingBodyId={actingBodyId}
            zIndex={3}
            disabled={false}
            includeDeleted={false}
            required
            labelStyle={styles.fieldsLabelStyle}
            viewStyle={{ gap: 8 }}
        />
        <View style={styles.datePickerStyle(estimatedStartZIndex, windowInfo.isMobile)}>
            <DateTimePicker
                date={form.watch('estimatedStart.date')}
                onChangeDate={(value) => form.setValue('estimatedStart.date', value)}
                time={form.watch('estimatedStart.hour')}
                onChangeTime={(value) => form.setValue('estimatedStart.hour', value)}
                label={translate('estimatedStart')}
                minDate={createdAt && unitId ? new Date(createdAt) : new Date()}
                labelFontSize={16}
                onCalendarOpen={() => setEstimatedStartZIndex(99999)}
                onCalendarClose={() => setEstimatedStartZIndex(2)}
                required
                labelStyle={styles.fieldsLabelStyle}
                viewStyle={{ gap: 8, flex: 1 }}
            />
        </View>
        <View style={styles.datePickerStyle(estimatedFinishZIndex, windowInfo.isMobile)}>
            <DateTimePicker
                date={form.watch('estimatedFinish.date')}
                onChangeDate={(value) => form.setValue('estimatedFinish.date', value)}
                time={form.watch('estimatedFinish.hour')}
                onChangeTime={(value) => form.setValue('estimatedFinish.hour', value)}
                label={translate('estimatedFinish')}
                minDate={createdAt && unitId ? new Date(createdAt) : new Date()}
                labelFontSize={16}
                onCalendarOpen={() => setEstimatedFinishZIndex(99999)}
                onCalendarClose={() => setEstimatedFinishZIndex(1)}
                required
                labelStyle={styles.fieldsLabelStyle}
                viewStyle={{ gap: 8, flex: 1 }}
            />
        </View>
        <FormInput
            label={translate('description')}
            placeholder={translate('description')}
            form={form}
            fieldName='description'
            multiline={true}
            numberOfLines={5}
            viewStyle={{ flex: 1 }}
            labelStyle={styles.fieldsLabelStyle}
        />
        <View style={{ flexDirection: 'column', gap: 8 }}>
            <View style={{ flexDirection: 'row', gap: 8, alignItems: 'flex-end' }}>
                {hasEquipmentCategories && <CustomButton
                    onPress={() => setAddByCategoryModal(!addByCategoryModal)}
                    text={translate('addByCategory')}
                    textStyle={{ fontSize: 12 }}
                    style={{ paddingHorizontal: 4, paddingVertical: 2, height: 'auto' }}
                />}
            </View>
            <EquipmentsDropdown
                form={form}
                fieldName='equipments'
                actingBodyId={actingBodyId}
                unitId={unitId}
                zIndex={2}
                labelStyle={styles.fieldsLabelStyle}
                dropdownLabelStyle={{ color: 'transparent' }}
                mode='SIMPLE'
                viewStyle={{ gap: 8 }}
            />
            <View style={{ marginTop: 8, gap: 8, zIndex: 1 }}>
                <MyAppText style={styles.fieldsLabelStyle}>{translate('selectedEquipments')}</MyAppText>
                <ScrollView style={{ maxHeight: 80 }} contentContainerStyle={{ flexDirection: 'row', columnGap: 8, rowGap: 4, flexWrap: 'wrap' }}>
                    {groupedEquipments.map((item, index) => (
                        <Badge
                            mode='ROUNDED'
                            key={index}
                            backgroundColor={theme.colors.backgrounds['BG-4']}
                            textColor={theme.colors.primary['500']}
                            removable={true}
                            onRemove={() => {
                                const itemToRemove = item.equipmentsIds.pop();
                                form.setValue('equipments', form.watch('equipments').filter(e => e != itemToRemove));
                            }}
                        >
                            {item.quantity ? `${item.name} - ${item.quantity}` : item.name}
                        </Badge>
                    ))}
                </ScrollView>
            </View>
        </View>
        <AddEquipmentByCategoryModal
            isModalVisible={addByCategoryModal}
            selectableEquipments={selectableEquipments}
            setModalVisible={setAddByCategoryModal}
            externalForm={form}
            onClose={() => setAddByCategoryModal(false)}
            actingBodyId={actingBodyId}
        />
    </View>;
}

const stylesheet = createStyleSheet((theme) => ({
    datePickerStyle: (zIndex: number, isMobile: boolean) => ({
        minWidth: 250,
        flexDirection: 'row',
        gap: 20,
        flexWrap: 'wrap',
        zIndex: zIndex,
        maxWidth: isMobile ? '100%' : '25%'
    }),
    fieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
}));
